import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo, type ReactNode } from 'react';

import operatorAuthService from '@services/operator/authService';

import operatorCable from '@/lib/operatorCable';

import type { Permission } from '@/permissions_manifest.generated';

export interface Operator {
  id: string;
  name?: string;
  username?: string;
  email?: string;
  organization_id?: string;
  warehouse?: unknown;
  permissions?: Permission[];
  totp_setup_required?: boolean;
  [key: string]: unknown;
}

interface AuthState {
  isAuthenticated: boolean;
  operator: Operator | null;
  permissions: Permission[];
  loading: boolean;
  error: string | null;
}

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { operator: Operator } }
  | { type: 'LOGIN_FAILURE'; payload: { error: string } }
  | { type: 'LOGOUT' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'CLEAR_ERROR' };

interface LoginResult {
  success: boolean;
  message?: string;
  totpRequired?: boolean;
  pendingToken?: string;
  totpSetupRequired?: boolean;
}

export interface OperatorAuthContextValue extends AuthState {
  login: (workspace: string, username: string, password: string) => Promise<LoginResult>;
  verifyTotp: (pendingToken: string, code: string) => Promise<LoginResult>;
  quickLogin: () => Promise<LoginResult>;
  logout: () => Promise<void>;
  clearError: () => void;
  checkAuthStatus: () => Promise<void>;
}

// 認證狀態
const initialState: AuthState = {
  isAuthenticated: false,
  operator: null,
  permissions: [], // Operator 權限清單 (例如：["products:read", "products:write"])
  loading: true,
  error: null
};

// Actions
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_LOADING: 'SET_LOADING',
  CLEAR_ERROR: 'CLEAR_ERROR'
} as const;

// Reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        loading: true,
        error: null
      };
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        isAuthenticated: true,
        operator: action.payload.operator,
        permissions: action.payload.operator?.permissions || [],
        loading: false,
        error: null
      };
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        isAuthenticated: false,
        operator: null,
        loading: false,
        error: action.payload.error
      };
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        isAuthenticated: false,
        operator: null,
        permissions: [],
        loading: false,
        error: null
      };
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    default:
      return state;
  }
}

// 創建Context
const OperatorAuthContext = createContext<OperatorAuthContextValue | undefined>(undefined);

// OperatorAuthProvider組件
export function OperatorAuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // 檢查認證狀態
  const checkAuthStatus = useCallback(async () => {
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });

    try {
      const result = await operatorAuthService.getCurrentOperator();
      if (result.success && result.data) {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { operator: result.data }
        });
      } else {
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      }
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  }, []);

  // 初始化檢查認證狀態
  useEffect(() => {
    // ✅ 在公開頁面（登入、邀請接受）不需要檢查認證狀態
    const isGuestPage = window.location.pathname.includes('/login') ||
                        window.location.pathname.includes('/invitations/');

    if (isGuestPage) {
      // 公開頁面：直接設置為未認證狀態，避免發送不必要的 API 請求
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    } else {
      // 需要認證的頁面：檢查認證狀態
      checkAuthStatus();
    }
  }, [checkAuthStatus]);

  // 登入
  const login = useCallback(async (workspace: string, username: string, password: string): Promise<LoginResult> => {
    // 🔥 不使用 LOGIN_START（會設 loading: true），避免 GuestGuard 卸載 Login 組件
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

    try {
      const result = await operatorAuthService.login(workspace, username, password);

      // TOTP 二步驟驗證：密碼正確但需要 TOTP
      if (result.success && result.data?.totp_required) {
        return {
          success: false,
          totpRequired: true,
          pendingToken: result.data.pending_token
        };
      }

      if (result.success && result.data) {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { operator: result.data }
        });
        return {
          success: true,
          message: result.message,
          totpSetupRequired: result.data.totp_setup_required || false
        };
      }
        // 使用後端返回的錯誤訊息碼，前端會透過 i18n 翻譯
        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAILURE,
          payload: { error: result.message || 'authentication_failed' }
        });
        return { success: false, message: result.message || 'authentication_failed' };

    } catch (error) {
      // 網路或其他錯誤時使用預設錯誤訊息碼
      const errorMessage = 'authentication_failed';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { error: errorMessage }
      });
      return { success: false, message: errorMessage };
    }
  }, []);

  // TOTP 驗證（登入第二步）
  const verifyTotp = useCallback(async (pendingToken: string, code: string): Promise<LoginResult> => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      const result = await operatorAuthService.verifyTotp(pendingToken, code);
      if (result.success && result.data) {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { operator: result.data }
        });
        return {
          success: true,
          message: result.message,
          totpSetupRequired: result.data.totp_setup_required || false
        };
      }
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { error: result.message || 'invalid_totp_code' }
      });
      return { success: false, message: result.message || 'invalid_totp_code' };
    } catch (error) {
      const errorMessage =
        (error as Error & { errorCode?: string }).errorCode || 'authentication_failed';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { error: errorMessage }
      });
      return { success: false, message: errorMessage };
    }
  }, []);

  // 快速登入（開發環境專用）
  const quickLogin = useCallback(async (): Promise<LoginResult> => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      const response = await fetch('/api/operator/v1/quick_login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await response.json();

      if (result.success && result.data) {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { operator: result.data }
        });
        return { success: true, message: result.message };
      }
        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAILURE,
          payload: { error: result.message || 'authentication_failed' }
        });
        return { success: false, message: result.message || 'authentication_failed' };

    } catch (error) {
      const errorMessage = 'authentication_failed';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { error: errorMessage }
      });
      return { success: false, message: errorMessage };
    }
  }, []);

  // 登出
  const logout = useCallback(async () => {
    try {
      await operatorAuthService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // ✅ 登出時強制斷開 WebSocket 連接
      operatorCable.forceDisconnect();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  }, []);

  // 清除錯誤
  const clearError = useCallback(() => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  }, []);

  // 權限檢查改由 useCan hook 統一處理（@shared/permissions/useCan）。
  // 不再從 context 暴露 hasPermission 方法。

  const value = useMemo<OperatorAuthContextValue>(() => ({
    ...state,
    login,
    verifyTotp,
    quickLogin,
    logout,
    clearError,
    checkAuthStatus
  }), [state, login, verifyTotp, quickLogin, logout, clearError, checkAuthStatus]);

  return (
    <OperatorAuthContext.Provider value={value}>
      {children}
    </OperatorAuthContext.Provider>
  );
}

// 自定義Hook
export function useOperatorAuth(): OperatorAuthContextValue {
  const context = useContext(OperatorAuthContext);
  if (!context) {
    throw new Error('useOperatorAuth must be used within an OperatorAuthProvider');
  }
  return context;
}

export default OperatorAuthContext;
